export const isInIframe = (): boolean => {
  try {
    return window && window.self !== window.top;
  } catch (e) {
    return false;
  }
};

// Best-effort host domain of the page embedding this iframe (e.g. 'gamma.app'),
// so we can tell the user which platform their presentation is running in.
// Prefers ancestorOrigins (Chromium/WebKit) and falls back to the referrer;
// returns null when not embedded or the host can't be determined (referrer
// stripped by policy). The leading 'www.' is dropped for a cleaner label.
export const getEmbedHost = (): string | null => {
  if (!isInIframe()) return null;
  try {
    const ancestors = window.location.ancestorOrigins;
    // The top-most ancestor is the actual host site (index 0 is the immediate
    // parent); fall back to document.referrer (the immediate parent's URL).
    const src = (ancestors && ancestors.length ? ancestors[ancestors.length - 1] : '')
      || window.document.referrer;
    if (!src) return null;
    const host = new URL(src).hostname;
    return host ? host.replace(/^www\./, '') : null;
  } catch {
    return null;
  }
};

// Known embedding platforms → the product name we show the user, so we can say
// "open Gamma" rather than a bare domain. A platform can serve from several
// domains (regional TLDs, dedicated embed/app subdomains), so each maps a list.
const EMBED_HOST_PLATFORMS: { name: string; domains: string[] }[] = [
  { name: 'Gamma', domains: ['gamma.app'] },
  { name: 'Canva', domains: ['canva.com', 'canva.cn'] },
  { name: 'Pitch', domains: ['pitch.com'] },
  { name: 'Prezi', domains: ['prezi.com'] },
  { name: 'Figma', domains: ['figma.com'] },
  { name: 'Google Slides', domains: ['docs.google.com', 'slides.google.com'] },
  { name: 'Beautiful.ai', domains: ['beautiful.ai'] },
  { name: 'Tome', domains: ['tome.app'] },
  { name: 'Genially', domains: ['genial.ly', 'genially.com'] },
  { name: 'Notion', domains: ['notion.so', 'notion.site'] },
  { name: 'Miro', domains: ['miro.com'] },
];

// Map an embed host to its platform name. Matched by domain suffix so
// subdomains (app.pitch.com) and regional variants resolve to the same name.
// Returns null for hosts we don't recognise.
export const hostToPlatformName = (host: string | null): string | null => {
  if (!host) return null;
  const h = host.toLowerCase();
  const match = EMBED_HOST_PLATFORMS.find(({ domains }) =>
    domains.some((d) => h === d || h.endsWith(`.${d}`)));
  return match ? match.name : null;
};

// User-facing label for the platform this presentation is embedded in: the known
// product name (e.g. 'Gamma') when we recognise the host, otherwise the bare
// host domain as a graceful fallback, or null when not embedded / undetectable.
export const getEmbedPlatform = (): string | null => {
  const host = getEmbedHost();
  return hostToPlatformName(host) || host;
};

export const isThirdPartyEmbedHost = (): boolean =>
  hostToPlatformName(getEmbedHost()) !== null;

export const getEmbedPlatformKey = (): string | undefined => {
  const name = hostToPlatformName(getEmbedHost());
  return name ? name.toLowerCase().replace(/[^a-z0-9]+/g, '-') : undefined;
};
